import RequisitesPayInDealSumsFormDTO from "@/models/form/RequisitesPayInDealSumsFormDTO";
import {PaymentOption} from "@/models/enum/PaymentOption";
import {PaymentMethod} from "@/models/enum/PaymentMethod";
import {$t} from "@/service/CommonHelper";
import TraderDTO from "@/models/TraderDTO";
import TraderDeviceDTO from "@/models/TraderDeviceDTO";
import AttachmentDTO from "@/models/AttachmentDTO";

export default interface RequisitesFormDTO extends RequisitesPayInDealSumsFormDTO {
    id: number|null;
    isActive: boolean;
    isWhiteTriangle: boolean;
    requisites: string|null;
    holderName: string|null;
    accountNumber: string|null;
    method: string|null;
    paymentOption: string|null;
    paymentSystem: string|null;
    currency: string|null;
    crossBorderCurrency: string|null;
    concurrentTradesCount: number|null;
    tradesTimeoutMinutes: number|null;
    dayTradesCountLimit: number|null;
    monthTradesCountLimit: number|null;
    dayTradesAllCountLimit: number|null;
    monthTradesAllCountLimit: number|null;
    dayTradesSumLimit: string|null;
    monthTradesSumLimit: string|null;
    totalTradesCountLimit: number|null;
    totalTradesSumLimit: string|null;
    manualResetEnabled: boolean;
    isFarmsSettingsEnabled: boolean;
    onlyInternalTransfer: boolean;
    multiplesOfValue: string|null;
    deviceId: string|null|TraderDeviceDTO;
    groupId: number|null;
    isAgreeBlacklistPaymentMethod: boolean|null;
    p2cConfig: any|null
    raw: any|null
    vietQrIsStatic?: boolean
    qrisIsStatic?: boolean
    pixIsStatic?: boolean
    maxReuseCount: number|null
    reuseDaysCount: number|null
    maxReuseCountEnable: boolean
    reusablePaymentsCountLimit: number|null
    dayTradesCount?: number
    monthTradesCount?: number
    monthLimitNextResetOn?: string|null
    dayTradesAllCount?: number
    monthTradesAllCount?: number
    dayTradesSum?: string|number
    monthTradesSum?: string|number
    totalTradesCount?: number
    totalTradesSum?: string|number
    trader?: TraderDTO|null
    mappingRowNum?: number|null
    file?: AttachmentDTO|null
}

export interface AdditionalField {
    name: string,
    type: string,
    label?: null|string,
    readonly?: boolean,
    values?: string[],
    customLabel?: any,
    dropzone?: any,
}

export const getAdditionalFields = (form: RequisitesFormDTO): AdditionalField[] => {
    if (form.paymentOption === PaymentOption.SBP_QR && form.method === PaymentMethod.avangard_pos) {
        return [
            {name: 'password', type: 'input'} as AdditionalField,
            {name: 'proxy', type: 'input'} as AdditionalField,
        ] as AdditionalField[]
    }

    if (form.paymentOption === PaymentOption.SBP_QR && form.method === PaymentMethod.pay_to_phone_emu) {
        return [
            {name: 'api_key', type: 'input'} as AdditionalField,
            {name: 'api_url', type: 'input'} as AdditionalField,
        ] as AdditionalField[]
    }

    if (form.paymentOption === PaymentOption.SBP_QR && form.method === PaymentMethod.qr_manager) {
        return [
            {name: 'api_key', type: 'input'} as AdditionalField,
            {name: 'api_login', type: 'input'} as AdditionalField,
        ] as AdditionalField[]
    }

    if ((form.paymentOption === PaymentOption.SBP_QR || form.paymentOption === PaymentOption.CARD_ACQUIRING_REDIRECT) && form.method === PaymentMethod.multitransfer) {
        return [
            {
                name: 'service_name', type: 'dropdown', values: [
                    'all_cards',
                    'to_account',
                    'UZB_CARD',
                    'UZB_UZCARD_HUMO',
                    //kgs
                    'mbank',
                    'companion_phone',
                    'companion_wallet',
                    'obank_phone',
                    'elkart_card',
                    //tjs
                    'tcell',
                    'my_tcell',
                    'dcb_phone',
                    'oriyonbonk_phone',
                    'alif_phone',
                    'active_phone',
                    'paikar_phone',
                    'paikar_wallet',
                    'spitamen_phone',
                    'VASL_CARD',
                    'SHUKR_MOLIYA_CARD',
                    //abh
                    'amobile_wallet',
                    //geo
                    'visa_mc_card_geo',
                    //azn
                    'visa_mc_card',
                    'm10_wallet',
                    'bank_respublika_card',
                    'rabitabank_card',
                    'kapitalbank_card',
                    //amd
                    'arca_visa_mc',
                    'mobidram_wallet',
                    'ARARAT_PHONE',

                    //TUR
                    'MULTI_TRANSFER_P',

                    //UZS
                    'POYTAXT_BANK_PHONE',
                    'POYTAXT_BANK_CARD',
                    'GARANTBANK_PHONE',
                    'GARANTBANK_CARD',
                    'OCTOBANK_PHONE',
                    'OCTOBANK_CARD',
                    'ASIA_ALLIANCE_PHONE',
                    'ASIA_ALLIANCE_CARD'
                ], customLabel: (value: string) => $t(`multitransfer.service.${value}`)
            } as AdditionalField,
        ] as AdditionalField[]
    }

    if (form.paymentOption === PaymentOption.SBP_QR && form.method === PaymentMethod.whitebird) {
        return [
            {name: 'password', type: 'input'} as AdditionalField,
            {name: '2fa_secret', type: 'input'} as AdditionalField,
        ] as AdditionalField[]
    }

    if (form.paymentOption === PaymentOption.SBP_QR && form.method === PaymentMethod.one_label) {
        return [
            {name: 'init_data', type: 'textarea'} as AdditionalField,
        ] as AdditionalField[]
    }

    if (form.paymentOption === PaymentOption.SBP_QR && form.method === PaymentMethod.vasl) {
        return [
            {name: 'http_request', type: 'textarea'} as AdditionalField,
            {name: 'device_id', type: 'input'} as AdditionalField,
            {name: 'passwordhash', type: 'input'} as AdditionalField,
            {name: 'hashcode', type: 'input'} as AdditionalField,
        ] as AdditionalField[]
    }

    if (form.paymentOption === PaymentOption.P2C) {
        switch (form.method) {
            case PaymentMethod.bkash_bdt:
                return [
                    {name: 'password', type: 'input'} as AdditionalField,
                    {name: 'app_key', type: 'input'} as AdditionalField,
                    {name: 'app_secret_key', type: 'input'} as AdditionalField,
                ] as AdditionalField[]
        }
    }

    return [] as AdditionalField[]
}